import { fetchSearchSynonym } from "app/(new-pages)/(main-page-new)/services";
import { getCountryKey } from 'helpers/country'

type Category = 'location' | 'region' | 'country' | 'remote_country' | 'job_type';

interface KeywordEntry {
  id: number;
  value: string;
  seo: string;
  synonyms: string[];
  region?: { seo: string };
  country?: { seo: string };
  remote_country?: { seo: string };
  continent?: { seo: string };
}

export type KeywordMap = {
  location: KeywordEntry[];
  region: KeywordEntry[];
  country: KeywordEntry[];
  remote_country: KeywordEntry[];
  job_type: KeywordEntry[];
  remote_synonyms: string[];
};

interface ClassificationResult {
  location?: string[];
  region?: string[];
  country?: string[];
  remote_country?: string[];
  jobType?: string;
  isRemote?: boolean;
  cleanedText?: string;
  query?: string;
}

// ------- Trie 实现 -------
class TrieNode {
  children: Record<string, TrieNode> = {};
  matches: { category: Category; entry: KeywordEntry; keyword: string }[] = [];
}

class Trie {
  private root = new TrieNode();

  insert(keyword: string, category: Category, entry: KeywordEntry) {
    let node = this.root;
    for (const char of keyword) {
      if (!node.children[char]) {
        node.children[char] = new TrieNode();
      }
      node = node.children[char];
    }
    node.matches.push({ category, entry, keyword });
  }

  scan(text: string): { category: Category; entry: KeywordEntry; keyword: string }[] {
    const result: { category: Category; entry: KeywordEntry; keyword: string }[] = [];
    const lowerText = text?.toLowerCase();

    for (let i = 0; i < lowerText.length; i++) {
      let node = this.root;
      let j = i;
      let bestMatch: { category: Category; entry: KeywordEntry; keyword: string } | null = null;

      while (j < lowerText.length && node.children[lowerText[j]]) {
        node = node.children[lowerText[j]];
        j++;

        if (node.matches.length > 0) {
          // 当前节点有匹配，找到最长 keyword 的那一个
          const longest = node.matches.reduce((a, b) =>
            b.keyword.length > a.keyword.length ? b : a
          );
          bestMatch = longest; // 每次更新为当前扫描路径中最长
        }
      }

      if (bestMatch) {
        result.push(bestMatch);
      }
    }

    return result;
  }
}

// ------- 主类 -------
export class FuzzyKeywordClassifier {
  private keywordMap: KeywordMap;
  private remoteSynonyms: string[];
  private trie: Trie;

  constructor(keywordMap: KeywordMap) {
    this.keywordMap = keywordMap;
    this.trie = new Trie();

    this.remoteSynonyms = keywordMap.remote_synonyms
      .map(s => s?.toLowerCase())
      .sort((a, b) => b.length - a.length);

    // 仅插入 synonyms
    (['location', 'region', 'job_type'] as Category[]).forEach(category => {
      this.keywordMap[category].forEach(entry => {
        entry.synonyms
          .filter(Boolean)
          .sort((a, b) => b.length - a.length) // 🔁 倒序，长的先插入
          .forEach(syn => {
            this.trie.insert(syn.toLowerCase(), category, entry);
          });
      });
    });
  }

  classify(text: string, detectRemote: boolean, isRemoteLocation: boolean): ClassificationResult {
    const lowerText = text?.toLowerCase();
    const result: ClassificationResult = {};
    const matched: Partial<Record<Category, KeywordEntry>> = {};
    const matchedSynonyms = new Set<string>();

    // remote
    result.isRemote = false;
    if (detectRemote) {
      for (const remote of this.remoteSynonyms) {
        if (lowerText?.includes(remote)) {
          matchedSynonyms.add(remote);
          result.isRemote = true;
          break;
        }
      }
    }

    // Trie match
    const matches = this.trie.scan(lowerText);
    const longestSynonyms: Partial<Record<Category, string>> = {};

    matches.forEach(({ category, entry, keyword }) => {
      const isRemoteRegionOrLocation = (result.isRemote || isRemoteLocation) && (category === 'region' || category === 'location');
      if (!isRemoteRegionOrLocation) {
        matchedSynonyms.add(keyword);
      }
      const prev = longestSynonyms[category];

      // 如果当前 keyword 更长，则替换 entry 和记录的 keyword
      if (!prev || keyword.length > prev.length) {
        matched[category] = entry;
        longestSynonyms[category] = keyword;
      }
    });

    // 组装结果
    const location = matched.location;
    const region = matched.region;
    const country = matched?.country;
    const remote_country = matched?.remote_country;
    const job = matched.job_type;

    if (!remote_country) {
      if (location) {
        result.location = [location.seo];
        result.region = region
          ? Array.from(new Set([location.region?.seo, region.seo].filter(Boolean))) as string[]
          : [location.region?.seo].filter(Boolean) as string[];
      } else if (region) {
        result.region = [region.seo];
        result.country = country
          ? Array.from(new Set([region.country?.seo, country.seo].filter(Boolean))) as string[]
          : [region.country?.seo].filter(Boolean) as string[];
      } else if (country) {
        result.country = [country.seo];
      }
    } else {
      if (remote_country) {
        result.location = [remote_country.seo];
        result.region = country
          ? Array.from(new Set([remote_country.continent?.seo, country.seo].filter(Boolean))) as string[]
          : [remote_country.continent?.seo].filter(Boolean) as string[];
      } else if (country) {
        result.region = [country.seo];
      }
    }

    if (job) {
      result.jobType = job.seo;
    }

    // 清理文本
    let cleanedText = text;
    const escapeRegExp = (str: string) => str.replace(/[.*+?^=!:${}()|[\]\\]/g, '\\$&');
    matchedSynonyms.forEach(syn => {
      const escaped = escapeRegExp(syn);
      const regex = new RegExp(escaped, 'ig');
      cleanedText = cleanedText.replace(regex, '');
    });
    result.cleanedText = cleanedText.trim().replace(/\s+/g, ' ');
    result.query = text;
    return result;
  }

  async classifyMultiple(texts: string[], detectRemote, isRemoteLocation: boolean): Promise<ClassificationResult[]> {
    return Promise.all(texts.map(text => this.classify(text, detectRemote, isRemoteLocation)));
  }

  detectSearchInput(inputValue: string, detectRemote: boolean, isRemoteLocation: boolean): ClassificationResult {
    return this.classify(inputValue, detectRemote, isRemoteLocation);
  }
}

const LOCAL_STORAGE_KEY = 'searchSynonymCache';
const CACHE_EXPIRE_DAYS = 1;

interface SynonymCache {
  data: KeywordMap;
  cachedAt: string; // ISO string
  countryKey: string;
}

const getCachedSynonym = (currentCountryKey: string): SynonymCache | null => {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as SynonymCache;
    const cachedDate = new Date(parsed.cachedAt);
    const now = new Date();

    const diffTime = now.getTime() - cachedDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    // ✅ 判断缓存是否过期或 countryKey 是否一致
    if (diffDays < CACHE_EXPIRE_DAYS && parsed.countryKey === currentCountryKey) {
      return parsed;
    }
  } catch (e) {
    console.warn('❌ Failed to parse synonym cache', e);
  }

  return null;
};

const saveSynonymToCache = (data: KeywordMap, countryKey: string) => {
  const payload: SynonymCache = {
    data,
    cachedAt: new Date().toISOString(),
    countryKey
  };
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
};

export const detectSearchInput = async (inputValue: string, detectRemote: boolean, isRemoteLocation = false) => {
  const countryKey = getCountryKey();
  let cached = getCachedSynonym(countryKey);
  let searchSynonym: KeywordMap;

  if (cached) {
    console.log('📦 using localStorage cached searchSynonym');
    searchSynonym = cached.data;
  } else {
    const searchSynonymResult = await fetchSearchSynonym();
    searchSynonym = searchSynonymResult?.data?.data;
    saveSynonymToCache(searchSynonym, countryKey);
    console.log('📦 refreshed searchSynonym localStorage cache');
  }

  const classifier = new FuzzyKeywordClassifier(searchSynonym);
  return classifier.detectSearchInput(inputValue, detectRemote, isRemoteLocation);
};